export interface TestIdGenerationConfig {
    [cssSelector: string]: string;
}

interface RunningElement {
    el: HTMLElement;
    retryThreadId: number;
}

let runningElements: RunningElement[] = [];
let retryThreadCounter = 0;

function setTestIDs(
    el: HTMLElement,
    config: TestIdGenerationConfig,
    retryThreadId = 0,
    retryCount = 0
) {
    if (!el) {
        console.error('Target element for test ID not found!');
        return;
    }
    retryThreadId = retryThreadId ? retryThreadId : ++retryThreadCounter;
    if (retryCount > 0) {
        if (
            runningElements.find((re) => re.el === el)?.retryThreadId !==
            retryThreadId
        ) {
            return;
        }
    }
    runningElements.push({ el, retryThreadId });

    const targets = new Array<{ el: HTMLElement; testId: string }>();
    for (const cssSelector of Object.keys(config)) {
        let target: HTMLElement | null;
        if (cssSelector) {
            target = el.querySelector(cssSelector);
        } else {
            target = el;
        }
        if (target) {
            targets.push({ el: target, testId: config[cssSelector] });
        } else {
            if (retryCount < 10) {
                setTimeout(
                    async () => {
                        await setTestIDs(
                            el,
                            config,
                            retryThreadId,
                            retryCount + 1
                        );
                    },
                    (retryCount + 1) * 100
                );
                return;
            } else {
                console.error(
                    `Target element for test ID "${config[cssSelector]}" not found by selector "${cssSelector}"!`
                );
                return;
            }
        }
    }
    if (targets) {
        targets.forEach((c) => c.el.setAttribute('data-test', c.testId));
    }
    runningElements = runningElements.filter((re) => re.el !== el);
}

function parseConfig(config: TestIdGenerationConfig | string) {
    if (typeof config === 'string') {
        return { '': config };
    } else {
        return config;
    }
}

export async function getTableRowTestIDs(
    rootTestId: string,
    columns: string[],
    rowCount: number
): Promise<TestIdGenerationConfig> {
    const testIDs: TestIdGenerationConfig = {
        '': rootTestId
    };

    for (let i = 0; i < columns.length; i++) {
        testIDs[`th:nth-child(${i + 1})`] =
            `${rootTestId}-header-${columns[i]}`;
    }

    const addTableRowTestID = (index: number, id: string) => {
        testIDs[`tbody tr:nth-child(${index + 1})`] = `${id}${index}`;
        let j = 0;
        columns.forEach((column: string) => {
            testIDs[`tbody tr:nth-child(${index + 1}) td:nth-child(${++j})`] =
                `${id}${index}-column-${column}`;
        });
    };
    for (let i = 0; i < rowCount; i++) {
        addTableRowTestID(i, `${rootTestId}-row-`);
    }
    return testIDs;
}

export async function tableTestIDs(
    rootTestId: string,
    columns: string[],
    rowCount: number,
    page: number,
    pageSize: number,
    totalElements: number
): Promise<TestIdGenerationConfig> {
    const maxPaginationButtons = 5; // 5 is the default pageLinkSize of the Paginator element of our DataTable
    const numberOfPages = Math.ceil(totalElements / pageSize);
    const numberOfPageButtons = Math.min(numberOfPages, maxPaginationButtons);
    const paginationTestIDs = {
        '.p-paginator-first': `${rootTestId}-pagination-first-page-button`,
        '.p-paginator-prev': `${rootTestId}-pagination-prev-page-button`,
        '.p-paginator-next': `${rootTestId}-pagination-next-page-button`,
        '.p-paginator-last': `${rootTestId}-pagination-last-page-button`,
        ...new Array(numberOfPageButtons)
            .fill(null)
            .reduce((acc, _val, idx) => {
                acc[`.p-paginator-page:nth-child(${idx + 1})`] =
                    `${rootTestId}-pagination-page-link-button-${idx + 1}`;
                return acc;
            }, {})
    };

    const tableRowTestIDs = await getTableRowTestIDs(
        rootTestId,
        columns,
        rowCount
    );

    const testIDs: TestIdGenerationConfig = {
        ...tableRowTestIDs,
        ...paginationTestIDs,
        '.p-datatable-table thead .p-checkbox-box': `${rootTestId}-select-all-checkbox`
    };

    for (let i = 0; i < columns.length; i++) {
        testIDs[`th:nth-child(${i + 1})`] =
            `${rootTestId}-header-${columns[i]}`;
    }

    const addTableRowTestID = (index: number, id: string) => {
        testIDs[`tbody tr:nth-child(${index + 1})`] = `${id}${index}`;
        let j = 0;
        columns.forEach((column: string) => {
            testIDs[`tbody tr:nth-child(${index + 1}) td:nth-child(${++j})`] =
                `${id}${index}-column-${column}`;
        });
    };
    for (let i = 0; i < rowCount; i++) {
        addTableRowTestID(i, `${rootTestId}-row-`);
    }
    return testIDs;
}

export const testId = {
    async mounted(
        el: HTMLElement,
        {
            value: config
        }: { value: TestIdGenerationConfig | string | undefined }
    ) {
        if (config) {
            setTestIDs(el, parseConfig(config));
        }
    },
    async beforeUpdate(
        el: HTMLElement,
        {
            value: config
        }: { value: TestIdGenerationConfig | string | undefined }
    ) {
        if (config) {
            setTestIDs(el, parseConfig(config));
        }
    }
};
